import { ReplaySubject } from 'rxjs';
import Kibana from '#/utils/trackKibana.js';
import scopeLogger from '#/lib/scope-logger.js';
import { performanceRate } from '#/lib/performance.rate.js';
import { tokenContent } from '#/lib/token-content.js';

const console = scopeLogger('RMI:ConfigProvider');

/**
 * @typedef {Object} Config
 * @property {Object} mns_config
 * @property {Object} user_info
 * @property {Object} key_codes
 * @property {Object} override
 * @property {string} token
 * @property {Object} api_config
 * @property {Array} extensions
 */
/**
 * @class ConfigProvider
 * @property {function} subscribe
 * @property {function} patchConfig
 */
export default class ConfigProvider {
  /* backend configuration override */
  #backend;
  /**
   * @type {ReplaySubject<Config>}
   */
  #config = new ReplaySubject(1);


  constructor({ session }) {
    console.log('create config provider');

    session.expose('backend', (persisted) => {
      this.#backend = persisted;
    });

    session.expose('config', (persisted) => {
      console.log('config from session storage', persisted);
      session.remove('config');
      if (this.#backend){
        console.log('apply backend override on self-reload');
        persisted.mns_config.broker_url = this.#backend.broker;
        persisted.api_config.url = this.#backend.api;
      } else {
        persisted.mns_config.broker_url = persisted.mns_config._broker_url;
        persisted.api_config.url = persisted.api_config._url;
      }
      this.#config.next(persisted);
    });
  }

  get hasConfig(){
    return this.#config._buffer.length > 0;
  }

  get config(){
    return this.#config;
  }

  get snapshot(){
    try {
      return this.#config._buffer[0];
    } catch (error){
      console.error('ConfigProvider snapshot error', error);
    }
  }

  get(propertyName){
    return this.#config[propertyName];
  }

  newConfig(newConfig) {
    const {
      mns_config: {
        user_info,
        api_url,
        video_config_url,
        extensions,
        settings_options,
        token,
        metrics_enabled,
        /*
        cms_url, // was used for getting landing by id, need no more?
        */

        ...mns_config
      },
      override,
      key_codes = {},
      logLevel = 'log',
    } = newConfig;

    const tokenInfo = tokenContent(token);
    user_info.profile_id = tokenInfo?.user_profile_id?.toString() ?? null;

    const api_config = {
      /* preserve original for restore */
      _url: api_url,
      /* use override or original as actual */
      url: this.#backend?.api ?? api_url,
      video_config_url: video_config_url.replace(api_url, ''),
      version: video_config_url?.match(/\/v(\d+)\//)?.[1],
    };

    mns_config.useSSL = window.location.protocol === 'https:';
    mns_config.port = mns_config.useSSL ? 443 : 80;
    mns_config.userName = 'inter-client';
    /* preserve original for restore */
    mns_config._broker_url = mns_config.broker_url;
    /* use override or original as actual */
    mns_config.broker_url = this.#backend?.broker ?? mns_config.broker_url;

    Kibana.setContext('user', {
      id: user_info.user_id,
      status: user_info.user_status,
    });
    Kibana.setContext('device', {
      id: user_info.device_id,
      type: user_info.interactive_platform,
      model: user_info.platform,
      performanceRate: performanceRate()
    });
    Kibana.setContext('session', mns_config.client_id);
    Kibana.setLevel(logLevel);

    const config = {
      mns_config,
      api_config,
      extensions: handleExtensionList(extensions, settings_options),
      user_info,
      key_codes,
      override,
      token,
      metrics_enabled
    };


    this.#config.next(config);
  }

  subscribe() {
    this.#config.subscribe(...arguments);
  }

  /**
   *
   * @param {string} path
   * @param {*} value
   */
  patchConfig(path, value) {
    const chain = path.split('.');
    const snapshot = this.snapshot;
    let temp = snapshot;

    try {
      while (chain.length > 1) {
        temp = temp[chain.shift()];
      }
      temp[chain.shift()] = value;

      this.#config.next(snapshot);
    } catch (error) {
      console.error('patchConfig error', error);
    }
  }
}

/**
 * @param {[Extension]} extensions
 * @param settings_options
 * @return {*}
 */
function handleExtensionList(extensions, settings_options) {
  if (import.meta.env.DEV) {
    const hasDebugLayer = extensions.some((extension) => extension.name === 'debug-layer');
    if (!hasDebugLayer) {
      extensions.push({
        name: 'debug-layer',
        src: '//interactive.qa.megogo.net/extensions/debug-layer/0.0.1/index.js',
        allowed: true,
        builtin: true,
        options: {
          "envs": [
            {
              "name": "qa",
              "api": "//api-interactive.qa.megogo.net",
              "broker": "edge-interactive.qa.megogo.net"
            },
            {
              "name": "dev qa",
              "api": "//api-interactive-dev.qa.megogo.net",
              "broker": "edge-interactive-dev.qa.megogo.net"
            },
            {
              "name": "rc",
              "api": "//api-interactive.rc.megogo.net",
              "broker": "edge-interactive.rc.megogo.net"
            },
            {
              "name": "prod",
              "api": "//api-interactive.megogo.net",
              "broker": "edge-interactive.megogo.net"
            }
          ]
        },
      });
    }
  }

  if (import.meta.env.DEV) {
    const extensionsSubstitute = new Map([
      ['poll', import.meta.env.VITE_APP_LOCAL_POLL_EXTENSION],
      ['promo-landing', import.meta.env.VITE_APP_LOCAL_PROMO_EXTENSION],
      ['quiz', import.meta.env.VITE_APP_LOCAL_QUIZ_EXTENSION],
      ['debug-layer', import.meta.env.VITE_APP_LOCAL_DEBUG_EXTENSION],
    ]);

    extensions.forEach((extension) => {
      const localSource = extensionsSubstitute.get(extension.name);
      if (localSource) {
        extension.src = localSource;
      }
    });
  }

  if (import.meta.env.DEV) {
    extensions.forEach((extension) => {
      if (['poll', 'quiz'].includes(extension.name)) {
        extension.options.dryRun = 'on';
      }
    });
  }

  const userSettings = new Map(
    settings_options.map(({ name, allowed }) => [name, allowed]),
  );

  return extensions.map((extension) => {
    const allowed = userSettings.get(extension.name);

    extension.options ??= {};
    extension.allowed ??= allowed ?? true;

    return {
      ...extension,
      /* May move this to consul, but we could modify only "option" property */
      barneyMode: [
        'promo-landing',
        // 'quiz',
        // 'poll',
      ].includes(extension.name),
    };
  });
}
